import { computed, inject, Signal } from '@angular/core';
import { L10N_LOCALE } from 'angular-l10n';
import { ApiConfig } from 'src/app/shared/api-config/api-config';
import { untilAborted } from 'src/app/shared/dashboard-grid';
import { MetricsApi } from 'src/app/usage-api';
import { MetricCatalog } from '../../data/metric-catalog';
import type { Metric, MetricLoader, MetricOption, MetricReading, MetricText } from './metrics';

/** Which Usage API Gauge the tile reads, by its catalog id such as `chat.roomsInUse` */
export type GaugeSettings = { gauge: string };

const GAUGE_ICON = 'speed';

/**
 * A Usage API Gauge, offered once per Gauge in the catalog. A Gauge has no time range, so the dashboard filters don't
 * apply to it.
 */
export const GAUGE_METRIC: Metric<GaugeSettings> = {
  label: 'Dashboard.Metric.Gauge',
  description: 'Dashboard.Metric.GaugeDescription',
  icon: GAUGE_ICON,
  filters: [],
  settings: {
    schema: {
      type: 'object',
      properties: { gauge: { type: 'string', minLength: 1 } },
      required: ['gauge'],
    },
    defaults: { gauge: '' },
    filtersFor: () => [],
  },
  injectLoader: injectGauge,
  injectLabel: injectGaugeLabel,
  injectOptions: injectGaugeOptions,
};

/** A Gauge's option value, kept apart from the built-in metric ids */
function gaugeOptionValue(id: string): string {
  return `gauge:${id}`;
}

/**
 * A Gauge's current value, added up across every Series the caller can read, against its Target. A Gauge query takes
 * no time range: the API refuses `from`, `to`, `interval` and `compareTo` for Gauges. A Gauge the loaded catalog
 * doesn't list isn't queried, as the API refuses Metrics the caller can't read.
 */
function injectGauge(): MetricLoader<GaugeSettings> {
  const metrics = inject(MetricsApi);
  const catalog = inject(MetricCatalog);
  const locale = inject(L10N_LOCALE);
  return async (_filters, { gauge }, abortSignal) => {
    if (catalog.loaded() && !catalog.findGauge(gauge)) {
      return {
        value: null,
        detail: { key: 'Dashboard.Metric.NoLongerAvailable' },
        warning: { key: 'Dashboard.Metric.Unavailable' },
      };
    }

    const response = await untilAborted(metrics.metricsQuery(ApiConfig.tenantId, { metrics: [gauge] }), abortSignal);
    const reading = response.rows?.[0]?.values?.[gauge];
    const deprecated: Pick<MetricReading, 'warning'> = response.deprecated?.includes(gauge)
      ? { warning: { key: 'Dashboard.Metric.Deprecated' } }
      : {};
    if (reading?.value == null) {
      return { value: null, detail: { key: 'Dashboard.Metric.NoValueYet' }, ...deprecated };
    }

    return { value: reading.value, detail: gaugeDetail(reading.target, reading.asOf, locale.language), ...deprecated };
  };
}

/** A Gauge is named by the catalog; its id stands in while the catalog loads, or when it no longer lists it */
function injectGaugeLabel(): (settings: GaugeSettings) => string {
  const catalog = inject(MetricCatalog);
  return ({ gauge }) => catalog.findGauge(gauge)?.name || gauge;
}

/** One option per Gauge still in use, as the catalog loads */
function injectGaugeOptions(): Signal<MetricOption<GaugeSettings>[]> {
  const catalog = inject(MetricCatalog);
  return computed(() =>
    catalog.gauges().map((gauge) => {
      const id = gauge.id ?? '';
      return {
        value: gaugeOptionValue(id),
        label: gauge.name || id,
        description: gauge.description ?? '',
        icon: GAUGE_ICON,
        ownSettings: { gauge: id },
      };
    }),
  );
}

/** The Target the value is read against, and when the value last changed */
function gaugeDetail(
  target: number | null | undefined,
  asOf: Date | string | null | undefined,
  language: string,
): MetricText | undefined {
  // The client types `asOf` as a Date but passes the JSON string through
  const updated = asOf ? updatedAt(new Date(asOf), language) : undefined;
  if (target == null) {
    return updated && { key: 'Dashboard.Metric.AsOf', params: { asOf: updated } };
  }

  return updated
    ? { key: 'Dashboard.Metric.OfTargetAsOf', params: { target, asOf: updated } }
    : { key: 'Dashboard.Metric.OfTarget', params: { target } };
}

/**
 * Each unit with its length in seconds, largest first; a month and a year are approximate.
 */
const RELATIVE_UNITS: { name: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
  { name: 'year', seconds: 365 * 24 * 60 * 60 },
  { name: 'month', seconds: 30 * 24 * 60 * 60 },
  { name: 'day', seconds: 24 * 60 * 60 },
  { name: 'hour', seconds: 60 * 60 },
  { name: 'minute', seconds: 60 },
];

/**
 * How long ago, in the largest whole unit, such as "5 hours ago"; under a minute reads "now".
 */
function updatedAt(date: Date, language: string): string {
  const elapsed = Math.max(0, (Date.now() - date.getTime()) / 1000);
  const unit = RELATIVE_UNITS.find((u) => elapsed >= u.seconds);
  if (!unit) {
    // `auto` words zero seconds as "now"
    return new Intl.RelativeTimeFormat(language, { numeric: 'auto' }).format(0, 'second');
  }

  // `always` keeps the count, as "1 day ago" rather than "yesterday": whole days elapsed aren't calendar days.
  // Negative for the past: -5 hours reads "5 hours ago"
  const format = new Intl.RelativeTimeFormat(language, { numeric: 'always' });
  return format.format(-Math.trunc(elapsed / unit.seconds), unit.name);
}
